import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import classNames from "classnames";
import { UseCases } from "@planzone/core";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveOrganization,
  useAuthentication,
  useRetrievePlanzoneSettings,
} from "@planzone/view-models";
import { TimeAllocation } from "@pages/planzoneParametersPage/timeAllocation";
import { Safeguard } from "@scenes/safeguard";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { IdentityProviders } from "@scenes/identityProviders";
import { AccessRestrictionsEdition } from "@scenes/accessRestrictionsEdition";
import { ProfileNavigation } from "@common/profileNavigation";
import { Props } from "./types";
import { ResourceUsageReport } from "./resourceUsageReport";
import { Organization } from "./organization";
import { Settings } from "./settings";
import styles from "./styles.scss";
import { CalendarsHolidays } from "./calendarsHolidays";
import { TimesheetDeadline } from "./timesheetDeadline";

function PlanzoneParametersPage(props: Props) {
  const { t } = useTranslation();
  const { planzone, hasOrganization } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { account } = useAuthentication();
  const {
    organization,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveOrganization,
  } = useRetrieveOrganization(planzone);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveOrganization(planzone);
  }, [planzone.uuid]);

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.planzoneParametersPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("planzoneParametersPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.main}>
          {hasOrganization && (
            <div className={styles.group}>
              <h2 className={styles.title}>
                {t("planzoneParametersPage.organization.title")}
              </h2>
              <div className={styles.section}>
                <Organization organization={organization} />
              </div>
            </div>
          )}
          <div className={styles.group}>
            <h2 className={styles.title}>
              {t("planzoneParametersPage.settings.title")}
            </h2>
            <div className={styles.section}>
              <Settings planzone={planzone} />
            </div>
          </div>
          <Safeguard useCases={[UseCases.editAccessRestrictions]}>
            {({ canEditAccessRestrictions }) =>
              canEditAccessRestrictions && (
                <div className={styles.group}>
                  <h2 className={styles.title}>
                    {t("planzoneParametersPage.areaRestrictions.title")}
                  </h2>
                  <div className={styles.section}>
                    <AccessRestrictionsEdition />
                  </div>
                </div>
              )
            }
          </Safeguard>
          <div className={styles.group}>
            <h2 className={styles.title}>
              {t("planzoneParametersPage.timeAllocation.title")}
            </h2>
            <div className={styles.section}>
              <TimeAllocation planzone={planzone} />
            </div>
          </div>
          <Safeguard useCases={[UseCases.createCalendarHolidays]}>
            {({ canCreateCalendarHolidays }) =>
              canCreateCalendarHolidays && (
                <div className={styles.group}>
                  <h2 className={styles.title}>
                    {t("planzoneParametersPage.calendarHolidays.title")}
                  </h2>
                  <div className={styles.section}>
                    <CalendarsHolidays planzone={planzone} />
                  </div>
                </div>
              )
            }
          </Safeguard>
          <div className={styles.group}>
            <h2 className={styles.title}>
              {t("planzoneParametersPage.logs.title")}
            </h2>
            <div className={styles.section} onClick={() => {}}>
              <NavLink
                to="/logs"
                className={classNames(styles.subLink, styles.subMenuItems)}
              >
                <p className={styles.redirectionContent}>
                  {t("planzoneParametersPage.logs.redirection")}
                </p>
              </NavLink>
            </div>
          </div>
          <div className={styles.group}>
            <h2 className={styles.title}>
              {t("planzoneParametersPage.timesheetDeadline.title")}
            </h2>
            <div className={styles.section}>
              <TimesheetDeadline planzone={planzone} />
            </div>
          </div>
          {settings.canUseResourceUsageReport && (
            <div className={styles.group}>
              <h2 className={styles.title}>
                {t("planzoneParametersPage.resourceUsageReport.title")}
              </h2>
              <div className={styles.section}>
                <ResourceUsageReport
                  fileName={t(
                    "planzoneParametersPage.resourceUsageReport.title",
                  )}
                  author={account}
                  planzone={planzone}
                />
              </div>
            </div>
          )}
          <div className={styles.group}>
            <h2 className={styles.title}>
              {t("planzoneParametersPage.api.title")}
            </h2>
            <div className={styles.section}>
              <div className={styles.apiSection}>
                <a
                  href="https://apis.planzone7.com/"
                  className={styles.apiLink}
                  target="_blank"
                >
                  {t("planzoneParametersPage.api.link")}
                </a>
              </div>
            </div>
          </div>
          {/*<Safeguard*/}
          {/*  useCases={[*/}
          {/*    UseCases.retrieveIdentityProviders,*/}
          {/*    UseCases.addIdentityProvider,*/}
          {/*  ]}*/}
          {/*>*/}
          {/*  {({ canAddIdentityProvider, canRetrieveIdentityProviders }) =>*/}
          {/*    canRetrieveIdentityProviders && (*/}
          {/*      <div className={styles.group}>*/}
          {/*        <h2 className={styles.title}>*/}
          {/*          {t("planzoneParametersPage.identityProviders.title")}*/}
          {/*        </h2>*/}
          {/*        <div className={styles.section}>*/}
          {/*          <IdentityProviders />*/}
          {/*        </div>*/}
          {/*      </div>*/}
          {/*    )*/}
          {/*  }*/}
          {/*</Safeguard>*/}
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { PlanzoneParametersPage };
